import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import {
  AddRnReclamationMessagePayload,
  CreateRnReclamationThreadPayload,
  RnReclamationThread,
  normalizeReclamationListResponse,
  normalizeReclamationResponse,
} from '../types/rnReclamationRequests';

interface ThreadFilter {
  rnWorkOrder: string;
}

const apiUrl = process.env.REACT_APP_API_URL ?? '';
const baseUrl = `${apiUrl}/api/rn-reclamation-requests`;

const bumpProNumber = (number: string): string => {
  const match = number.match(/^(.*)\/PRO\/(\d+)$/i);
  if (!match) return '';
  return `${match[1]}/PRO/${String(Number(match[2]) + 1).padStart(2, '0')}`;
};

export const useRnReclamationRequests = () => {
  const { showToast } = useToast();
  const [threads, setThreads] = useState<RnReclamationThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [nextNumber, setNextNumber] = useState('');
  const filterRef = useRef<ThreadFilter | null>(null);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'x-auth-token': token || '', 'Content-Type': 'application/json' };
  };

  const loadThreads = useCallback(async (filter: ThreadFilter, targetPage = 1) => {
    filterRef.current = filter;
    setLoading(true);
    try {
      const response = await axios.get(baseUrl, {
        headers: authHeaders(),
        params: { rnWorkOrder: filter.rnWorkOrder, page: targetPage },
      });
      const envelope = normalizeReclamationListResponse(response.data);
      setThreads((prev) => (targetPage === 1 ? envelope.data : [...prev, ...envelope.data]));
      setPage(envelope.page);
      setTotalPages(envelope.totalPages);
      if (targetPage === 1) {
        if (envelope.nextNumber) {
          setNextNumber(envelope.nextNumber);
        } else {
          const maxAssigned = envelope.data
            .map((thread) => thread.number || '')
            .filter(Boolean)
            .sort((a, b) => {
              const aSeq = Number((a.match(/\/PRO\/(\d+)$/i) || [])[1] || 0);
              const bSeq = Number((b.match(/\/PRO\/(\d+)$/i) || [])[1] || 0);
              return bSeq - aSeq;
            })[0];
          if (maxAssigned) setNextNumber(bumpProNumber(maxAssigned));
        }
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to load reclamations';
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const loadMore = useCallback(async () => {
    if (!filterRef.current || page >= totalPages) return;
    await loadThreads(filterRef.current, page + 1);
  }, [loadThreads, page, totalPages]);

  const createThread = useCallback(async (payload: CreateRnReclamationThreadPayload): Promise<RnReclamationThread | null> => {
    try {
      const response = await axios.post<unknown>(baseUrl, payload, { headers: authHeaders() });
      const thread = normalizeReclamationResponse(response.data);
      if (!thread) {
        showToast('Failed to send reclamation', 'error');
        return null;
      }
      setThreads((prev) => [thread, ...prev]);
      const envelope = response.data as { nextNumber?: string };
      if (typeof envelope.nextNumber === 'string' && envelope.nextNumber) {
        setNextNumber(envelope.nextNumber);
      } else if (thread.number) {
        setNextNumber(bumpProNumber(thread.number));
      }
      return thread;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to send reclamation';
      showToast(message, 'error');
      return null;
    }
  }, [showToast]);

  const addMessage = useCallback(async (threadId: string, text: string, dateTo?: string | null): Promise<RnReclamationThread | null> => {
    try {
      const payload: AddRnReclamationMessagePayload = { text };
      if (dateTo !== undefined) payload.dateTo = dateTo;
      const response = await axios.post<unknown>(`${baseUrl}/${threadId}/messages`, payload, { headers: authHeaders() });
      const thread = normalizeReclamationResponse(response.data);
      if (!thread) {
        showToast('Failed to send reply', 'error');
        return null;
      }
      setThreads((prev) => prev.map((existing) => (existing._id === thread._id ? thread : existing)));
      return thread;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to send reply';
      showToast(message, 'error');
      return null;
    }
  }, [showToast]);

  const closeThread = useCallback(async (threadId: string): Promise<RnReclamationThread | null> => {
    try {
      const response = await axios.patch<unknown>(`${baseUrl}/${threadId}/close`, {}, { headers: authHeaders() });
      const thread = normalizeReclamationResponse(response.data);
      if (!thread) {
        showToast('Failed to close reclamation', 'error');
        return null;
      }
      setThreads((prev) => prev.map((existing) => (existing._id === thread._id ? thread : existing)));
      return thread;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to close reclamation';
      showToast(message, 'error');
      return null;
    }
  }, [showToast]);

  const refreshNextNumber = useCallback(async (rnWorkOrder: string) => {
    try {
      const response = await axios.get(`${baseUrl}/next-number`, {
        headers: authHeaders(),
        params: { rnWorkOrder },
      });
      const payload = response.data as { data?: { number?: string }; number?: string };
      const value = payload?.data?.number || payload?.number || '';
      if (value) setNextNumber(value);
      return value;
    } catch {
      return '';
    }
  }, []);

  return {
    threads,
    loading,
    page,
    totalPages,
    nextNumber,
    setNextNumber,
    loadThreads,
    loadMore,
    createThread,
    addMessage,
    closeThread,
    refreshNextNumber,
  };
};
